import {
  useCallback,
  useRef,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Button, Dialog, Flex } from '@servicetitan/anvil2';
import styles from './SimpleConfirmationModal.module.css';

type FocusResolver = NonNullable<
  ComponentProps<typeof Dialog>['initialFocusResolver']
>;

export interface SimpleConfirmationModalProps {
  title?: string;
  body?: ReactNode;
  isOpen: boolean;
  primaryText?: string;
  /** Defaults to `danger`. */
  primaryAppearance?: 'primary' | 'danger';
  secondaryText?: string;
  /**
   * Focuses the title on open instead of the first focusable, so a screen
   * reader announces it and Tab starts at the body.
   */
  focusTitleOnOpen?: boolean;
  /** Test ids for the dialog and its buttons; the dialog defaults to `confirmation-modal`. */
  testIds?: { dialog?: string; primary?: string; secondary?: string };
  onPrimaryClick?: () => void;
  onSecondaryClick?: () => void;
  onClose?: () => void;
}

const SimpleConfirmationModal = ({
  title,
  isOpen,
  body,
  primaryText,
  primaryAppearance = 'danger',
  secondaryText,
  focusTitleOnOpen = false,
  testIds,
  onPrimaryClick,
  onSecondaryClick,
  onClose,
}: SimpleConfirmationModalProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const focusTitle = useCallback<FocusResolver>(
    (focusables) => headerRef.current ?? focusables[0],
    [],
  );
  const shouldFocusTitle = focusTitleOnOpen && !!title;

  return (
    <Dialog
      open={isOpen}
      data-testid={testIds?.dialog ?? 'confirmation-modal'}
      className={styles.dialogContainer}
      onClose={onClose}
      initialFocusResolver={shouldFocusTitle ? focusTitle : undefined}
    >
      {title && (
        <Dialog.Header
          ref={headerRef}
          className={styles.header}
          tabIndex={shouldFocusTitle ? -1 : undefined}
        >
          {title}
        </Dialog.Header>
      )}
      <Dialog.Content>{body}</Dialog.Content>
      <Dialog.Footer>
        <Flex gap='3' justifyContent='flex-end'>
          <Dialog.CancelButton
            onClick={onSecondaryClick}
            // anvil2 names every cancel button "Close dialog" unless told otherwise;
            // keep that name when there is no text to use instead
            {...(secondaryText ? { 'aria-label': secondaryText } : {})}
            data-testid={testIds?.secondary}
          >
            {secondaryText}
          </Dialog.CancelButton>
          <Button
            appearance={primaryAppearance}
            onClick={onPrimaryClick}
            data-testid={testIds?.primary}
          >
            {primaryText}
          </Button>
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
};

export default SimpleConfirmationModal;
